import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import {
  BrandSearchResult,
  DataAccessExhibitorsPageFacade,
  DataAccessFairsFacade,
  ExhibitorSearchResult,
  ExhibitorSearchResultBoothType,
  ExhibitorSearchResultFlags,
} from '@planone/bau365/data-access-fair-data';
import { ShareFacade } from '@planone/bau365/feature-tracking';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { ModalBottomsheetHelper, ModalBottomsheetType, ShareButtonsHelper } from '@planone/shared/ui-elements';
import { localizedMeta } from '@planone/shared/util-meta';
import { ImageFileTypes } from '@planone/shared/util-pipelines';
import { map, Observable, Subject, take } from 'rxjs';

@Component({
  selector: 'bau365-directory-list-item',
  templateUrl: './directory-list-item.component.html',
  styleUrls: ['./directory-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DirectoryListItemComponent implements OnChanges, OnDestroy {
  @Input()
  entry?: ExhibitorSearchResult | BrandSearchResult;

  @Input()
  fairId: string | null | undefined;

  @Input()
  size: string | undefined | null;

  @Input()
  watchlistEnabled: boolean | undefined | null = false;

  @Input()
  isPlatformFair = true;

  url: string | undefined;
  emailSubject = localizedMeta['shareEmailSubject'];
  emailBody = localizedMeta['shareEmailBody'];

  fairId$: Observable<string>;
  hallPlanToggle$: Observable<boolean | undefined>;
  hallPlanActive$: Observable<boolean | undefined>;

  type = ExhibitorSearchResultBoothType;
  ImageFileTypes = ImageFileTypes;
  ExhibitorSearchResultFlags = ExhibitorSearchResultFlags;

  private unsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('shareButtonsTitle') public shareButtonsTitle: TemplateRef<never> | null | undefined;
  @ViewChild('shareButtons') public shareButtons: TemplateRef<never> | null | undefined;

  constructor(
    private ExhibitorsPageFacade: DataAccessExhibitorsPageFacade,
    private trackingShareFacade: ShareFacade,
    private fairsFacade: DataAccessFairsFacade,
    private shareButtonsHelper: ShareButtonsHelper,
    private modalBottomsheetHelper: ModalBottomsheetHelper,
    public togglesFacade: DataAccessTogglesFacade
  ) {
    this.fairId$ = this.fairsFacade.fairId$;
    this.hallPlanToggle$ = togglesFacade.flag('kill-hall-plan').pipe(map((hallplanDisabled) => !hallplanDisabled));
    this.hallPlanActive$ = fairsFacade.fairConfig$.pipe(map((fair) => !!fair?.hallplan));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['entry'] || changes['fairId']) {
      if (this.entry?.type === 'EXHIBITOR') {
        this.url = this.shareButtonsHelper.buildShareUrlFromPath(
          'fairs/' + this.fairId + '/exhibitors/' + this.entry?.exhibitor?.id
        );
        this.emailBody += $localize`:@@pageExhibitorDirectory-emailShare-entityType-exhibitor:Aussteller ${this.entry?.exhibitor?.name}`;
      } else if (this.entry?.type === 'BRAND') {
        this.url = this.shareButtonsHelper.buildShareUrlFromPath(
          'fairs/' + this.fairId + '/exhibitors/' + this.entry?.brand?.id + '?brand=true'
        );
        this.emailBody += $localize`:@@pageExhibitorDirectory-emailShare-entityType-brand:Marke ${this.entry?.brand?.name}`;
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  openShareButtonDialog(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.entry) {
        if (this.entry?.type === 'EXHIBITOR') {
          this.ExhibitorsPageFacade.shareDialogOpened('EXHIBITOR', 'exhibitors', fairId, this.entry?.exhibitor);

          this.trackingShareFacade.open({
            entityType: 'EXHIBITOR_SEARCH_RESULT',
            pageType: 'exhibitors',
            context: fairId,
            entities: {
              search_result: this.entry,
            },
          });
        } else if (this.entry?.type === 'BRAND') {
          this.ExhibitorsPageFacade.shareDialogOpened('BRAND', 'exhibitors', fairId, this.entry?.brand);

          this.trackingShareFacade.open({
            entityType: 'BRAND_SEARCH_RESULT',
            pageType: 'exhibitors',
            context: fairId,
            entities: {
              search_result: this.entry,
            },
          });
        }
      }
    });

    this.modalBottomsheetHelper.openDialog(
      ModalBottomsheetType.share,
      this.shareButtonsTitle,
      this.shareButtons,
      this.size ?? undefined
    );
  }

  shareButtonClick(network: string) {
    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      if (this.entry) {
        if (this.entry?.type === 'EXHIBITOR') {
          this.ExhibitorsPageFacade.shareButtonClicked(
            network,
            this.entry.exhibitor,
            fairId,
            'exhibitors',
            'EXHIBITOR'
          );

          this.trackingShareFacade.click({
            entityType: 'EXHIBITOR_SEARCH_RESULT',
            pageType: 'exhibitors',
            context: fairId,
            network: network,
            entities: {
              search_result: this.entry,
            },
          });
        } else if (this.entry?.type === 'BRAND') {
          this.ExhibitorsPageFacade.shareButtonClicked(network, this.entry.brand, fairId, 'exhibitors', 'BRAND');

          this.trackingShareFacade.click({
            entityType: 'BRAND_SEARCH_RESULT',
            pageType: 'exhibitors',
            context: fairId,
            network: network,
            entities: {
              search_result: this.entry,
            },
          });
        }
      }
    });
  }
}
